import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Wand2, Save, Plus, Trash2, MousePointer2 } from "lucide-react";

const FIELD_KINDS = [
  { value: "text",             label: "Text" },
  { value: "date",             label: "Date" },
  { value: "signature",        label: "Signature (dealer)" },
  { value: "signature_admin",  label: "Signature (AWG)" },
  { value: "checkbox",         label: "Checkbox" },
  { value: "initial",          label: "Initial" },
];
const FILLED_BY = [
  { value: "dealer", label: "Dealer fills" },
  { value: "admin",  label: "AWG fills" },
  { value: "auto",   label: "Auto-fill" },
];

const KIND_COLORS = {
  text: "border-blue-500 bg-blue-500/10",
  date: "border-cyan-500 bg-cyan-500/10",
  signature: "border-[#D4AF37] bg-[#D4AF37]/15",
  signature_admin: "border-purple-500 bg-purple-500/15",
  checkbox: "border-green-500 bg-green-500/10",
  initial: "border-orange-500 bg-orange-500/10",
};

export default function AdminDealerAgreement() {
  const { formId } = useParams();
  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldKeys, setFieldKeys] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [draggingId, setDraggingId] = useState(null);
  const dragOffset = useRef({ dx: 0, dy: 0 });

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/dealers/forms/${formId}`);
      setMeta(data);
      setFields(data.fields || []);
    } catch (e) {
      toast.error("Form not found — head back to the library");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [formId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/dealer-field-keys");
        setFieldKeys(data?.all || []);
      } catch (err) {
        console.error("[AdminDealerAgreement] field-keys load failed:", err?.message || err);
      }
    })();
  }, []);

  const currentPage = meta?.pages?.find((p) => p.index === activePage);
  const pageFields = fields.filter((f) => f.page === activePage);

  const autoDetect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post(`/admin/dealers/forms/${formId}/auto-detect`);
      setFields(data.fields);
      toast.success(`Auto-detected ${data.count} field(s) — review + adjust as needed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Detection failed"); }
    finally { setDetecting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/dealers/forms/${formId}/fields`, { fields });
      toast.success("Fields saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addField = () => {
    const id = `f_${Date.now()}`;
    setFields((prev) => [
      ...prev,
      { id, page: activePage, x: 100, y: 400, width: 180, height: 22, kind: "text", filled_by: "dealer", label: "New field", required: false },
    ]);
    setSelectedId(id);
  };

  const patchField = (id, patch) => setFields((prev) => prev.map((f) => f.id === id ? { ...f, ...patch } : f));
  const removeField = (id) => { setFields((prev) => prev.filter((f) => f.id !== id)); if (selectedId === id) setSelectedId(null); };

  const selected = fields.find((f) => f.id === selectedId);

  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);
  const onImgLoad = () => {
    if (imgRef.current) setImgSize({ w: imgRef.current.clientWidth, h: imgRef.current.clientHeight });
  };
  const pdfW = currentPage?.width || 612;
  const pdfH = currentPage?.height || 792;
  const rx = imgSize.w / pdfW;
  const ry = imgSize.h / pdfH;

  const boxStyle = (f) => ({
    position: "absolute",
    left: `${f.x * rx}px`,
    top: `${(pdfH - f.y - f.height) * ry}px`,
    width: `${f.width * rx}px`,
    height: `${f.height * ry}px`,
    cursor: "move",
  });

  const onMouseDownField = (e, f) => {
    e.stopPropagation();
    setSelectedId(f.id);
    setDraggingId(f.id);
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
  };
  const onMouseMoveContainer = (e) => {
    if (!draggingId || !imgRef.current) return;
    const cRect = imgRef.current.getBoundingClientRect();
    const cssX = e.clientX - cRect.left - dragOffset.current.dx;
    const cssY = e.clientY - cRect.top - dragOffset.current.dy;
    const newX = Math.max(0, cssX / rx);
    const newY = Math.max(0, pdfH - (cssY / ry) - (fields.find(f => f.id === draggingId)?.height || 20));
    patchField(draggingId, { x: Math.round(newX * 10) / 10, y: Math.round(newY * 10) / 10 });
  };
  const onMouseUpContainer = () => setDraggingId(null);

  if (!meta) return <div className="min-h-screen bg-[#F8F9FA]"><Navbar /><main className="max-w-5xl mx-auto px-6 py-10 text-sm text-[#6C757D]">Loading form…</main></div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8" data-testid="admin-dealer-agreement">
        <Link to="/admin/dealer-forms" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-4"><ArrowLeft size={14}/> Forms library</Link>
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Field mapping</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B132B] mt-1 tracking-tight leading-none">{meta.name}</h1>
            <p className="text-sm text-[#6C757D] mt-2 max-w-2xl">{meta.filename} · {meta.page_count} pages · {fields.length} field(s) mapped</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={autoDetect} disabled={detecting} variant="outline" className="h-10 rounded-sm border-[#0B132B]/20" data-testid="auto-detect-btn">
              <Wand2 size={14} className="mr-2"/> {detecting ? "Scanning…" : "Auto-detect fields"}
            </Button>
            <Button onClick={save} disabled={saving} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="save-fields-btn">
              <Save size={14} className="mr-2"/> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
          <div>
            <div className="flex gap-1 overflow-x-auto border-b border-[#E9ECEF] mb-3" data-testid="page-tabs">
              {(meta.pages || []).map((p) => {
                const cnt = fields.filter((f) => f.page === p.index).length;
                return (
                  <button key={p.index} type="button" onClick={() => { setActivePage(p.index); setSelectedId(null); }}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.14em] font-bold whitespace-nowrap border-b-2 transition-colors ${
                      activePage === p.index ? "border-[#D4AF37] text-[#0B132B]" : "border-transparent text-[#6C757D] hover:text-[#0B132B]"
                    }`}
                    data-testid={`page-tab-${p.index}`}>
                    Page {p.index} {cnt > 0 && <span className="ml-1 text-[#D4AF37]">·{cnt}</span>}
                  </button>
                );
              })}
            </div>
            <Card className="p-3 border-[#E9ECEF] rounded-sm overflow-hidden">
              <div className="relative bg-[#F8F9FA] flex justify-center" onMouseMove={onMouseMoveContainer} onMouseUp={onMouseUpContainer} onMouseLeave={onMouseUpContainer}>
                <div className="relative" style={{ maxWidth: "100%" }}>
                  <img
                    ref={imgRef}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dealers/forms/${formId}/page/${activePage}.png`}
                    onLoad={onImgLoad}
                    alt={`Page ${activePage}`}
                    className="block max-w-full h-auto shadow-md"
                    data-testid={`page-image-${activePage}`}
                  />
                  {pageFields.map((f) => (
                    <div key={f.id}
                      onMouseDown={(e) => onMouseDownField(e, f)}
                      className={`border-2 rounded-sm transition-shadow ${KIND_COLORS[f.kind] || "border-gray-400 bg-gray-100/20"} ${selectedId === f.id ? "ring-2 ring-offset-1 ring-[#D4AF37] shadow-lg" : ""}`}
                      style={boxStyle(f)}
                      data-testid={`field-box-${f.id}`}
                      title={f.label}
                    >
                      <div className="text-[9px] font-bold uppercase tracking-tight text-[#0B132B] bg-white/80 px-1 truncate leading-tight">{f.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-[#6C757D] mt-2 flex items-center gap-2">
                <MousePointer2 size={12}/> Drag any field to reposition · Click to select · Use panel to resize / delete
              </div>
            </Card>
          </div>

          <div className="space-y-3">
            <Card className="p-4 border-[#E9ECEF] rounded-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Fields on this page</div>
                <Button size="sm" variant="outline" onClick={addField} className="h-8 rounded-sm border-[#0B132B]/20" data-testid="add-field-btn">
                  <Plus size={12} className="mr-1"/> Add
                </Button>
              </div>
              <div className="max-h-[280px] overflow-y-auto space-y-1">
                {pageFields.length === 0 ? (
                  <div className="text-xs text-[#6C757D] italic py-4 text-center">No fields on page {activePage}. Click Auto-detect or Add.</div>
                ) : pageFields.map((f) => (
                  <button key={f.id} type="button" onClick={() => setSelectedId(f.id)}
                    className={`w-full flex items-center justify-between p-2 rounded-sm text-left transition-colors ${
                      selectedId === f.id ? "bg-[#0B132B] text-white" : "bg-white hover:bg-[#F8F9FA] text-[#0B132B]"
                    }`}
                    data-testid={`field-list-${f.id}`}>
                    <span className="truncate text-xs font-semibold">{f.label}</span>
                    <span className={`text-[9px] uppercase tracking-[0.14em] ml-2 shrink-0 ${selectedId === f.id ? "text-white/70" : "text-[#6C757D]"}`}>{f.kind}</span>
                  </button>
                ))}
              </div>
            </Card>

            {selected && (
              <Card className="p-4 border-[#0B132B]/30 rounded-sm" data-testid="field-editor-panel">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-3">Edit field</div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[10px] uppercase font-bold tracking-[0.16em]">Label</Label>
                    <Input value={selected.label} onChange={(e) => patchField(selected.id, { label: e.target.value })} className="mt-1 h-9 rounded-sm" data-testid="field-label-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase font-bold tracking-[0.16em]">Kind</Label>
                      <Select value={selected.kind} onValueChange={(v) => patchField(selected.id, { kind: v })}>
                        <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="field-kind-select"><SelectValue/></SelectTrigger>
                        <SelectContent>{FIELD_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold tracking-[0.16em]">Filled by</Label>
                      <Select value={selected.filled_by} onValueChange={(v) => patchField(selected.id, { filled_by: v })}>
                        <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="field-filled-by-select"><SelectValue/></SelectTrigger>
                        <SelectContent>{FILLED_BY.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase font-bold tracking-[0.16em]">Field key <span className="text-[#6C757D] normal-case tracking-normal">(dedupe)</span></Label>
                    <Select
                      value={selected.field_key || "__none__"}
                      onValueChange={(v) => patchField(selected.id, { field_key: v === "__none__" ? null : v })}
                    >
                      <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="field-key-select"><SelectValue placeholder="No key" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">— No key (asked once, unique) —</SelectItem>
                        {fieldKeys.map((k) => (
                          <SelectItem key={k.key} value={k.key} data-testid={`field-key-opt-${k.key}`}>
                            {k.label} <span className="text-[10px] text-[#6C757D] ml-2">· {k.group}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#6C757D] mt-1 leading-snug">Fields sharing a key are filled by one dealer input and stamped everywhere.</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {["x","y","width","height"].map((k) => (
                      <div key={k}>
                        <Label className="text-[10px] uppercase font-bold tracking-[0.16em]">{k}</Label>
                        <Input type="number" value={Math.round(selected[k])} onChange={(e) => patchField(selected.id, { [k]: Number(e.target.value) })} className="mt-1 h-9 rounded-sm font-mono text-xs" data-testid={`field-${k}-input`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!selected.required} onChange={(e) => patchField(selected.id, { required: e.target.checked })} data-testid="field-required-input" />
                    <span className="text-xs text-[#0B132B]">Required</span>
                  </div>
                  <button type="button" onClick={() => removeField(selected.id)} className="w-full mt-2 h-9 rounded-sm text-xs uppercase tracking-[0.14em] font-bold text-red-600 border border-red-200 hover:bg-red-50" data-testid="delete-field-btn">
                    <Trash2 size={12} className="inline mr-1.5 -mt-0.5"/> Delete field
                  </button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
